import { EmergencyResourceDataSearchResultsViewModel } from 'api'
import * as React from 'react'
import { Grid, Typography } from 'cs-kevlar-react'
import { Permissions } from '../../Permissions'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'

interface EmergencyResourceProps {
  result: EmergencyResourceDataSearchResultsViewModel
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    key: {
      color: theme.palette.neutralButton.main,
    },
    marginTop: {
      marginTop: theme.spacing(10),
    },
  }),
)

const formatDate = (date: string | undefined) => {
  return date ? new Date(date).toUTCString() : ''
}

const formatDeviceAvlDateOrMessage = (
  result: EmergencyResourceDataSearchResultsViewModel,
) => {
  if (!result.deviceAvlMonitoringEnabled) {
    return 'Cloud monitoring of device AVL pushes is disabled.'
  }

  const date = formatDate(result.avlLastUpdatedFromFoDevice)
  if (date) return date

  const avlMonitoringStarted = formatDate(result.deviceAvlMonitoringStartedAt)
  return `None since ${avlMonitoringStarted}`
}

export const EmergencyResource: React.FunctionComponent<EmergencyResourceProps> = (
  props,
) => {
  const classes = useStyles({})
  const { result } = props

  // use UTC here to avoid confusion between support looking at this from a different timezone as the CAD
  // the output string says GMT so they should be able to tell what timezone it is
  const avlCadDate = formatDate(result.avlLastUpdatedFromCad)
  const avlDeviceDate = formatDeviceAvlDateOrMessage(result)

  return (
    <React.Fragment>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:type"
        >
          Type
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:id"
        >
          Id
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:agency"
        >
          Agency
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:status"
        >
          Status
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:isActive"
        >
          Is Active
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:assignedToId"
        >
          Assigned
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:avlLastUpdatedFromCad"
        >
          AVL Last Update from CAD
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          className={classes.key}
          data-test="DataSearch:ER:Key:avlLastUpdatedFromFoDevice"
        >
          AVL Last Update from FO Device
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:type"
        >
          {result.type}
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:id"
        >
          {result.id}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:agency"
        >
          {result.agency}
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:status"
        >
          {result.status}
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:isActive"
        >
          {result.isActive}
        </Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:assignedToId"
        >
          {result.assignedToId}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:avlLastUpdatedFromCad"
        >
          {avlCadDate}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography
          variant="body2"
          display="inline"
          data-test="DataSearch:ER:Value:avlLastUpdatedFromFoDevice"
        >
          {avlDeviceDate}
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.marginTop}>
        <Permissions
          permissions={JSON.parse(result.permissions)}
          data-test="DataSearch:ER:Value:permissions"
        />
      </Grid>
    </React.Fragment>
  )
}
